import { useCallback, useMemo, useReducer } from 'react';

export type LoadingHook<T, E> = [T | undefined, boolean, E | undefined];

export type LoadingValue<T, E> = {
    error?: E;
    loading: boolean;
    reset: () => void;
    setError: (error: E) => void;
    setValue: (value?: T) => void;
    value?: T;
};

type ReducerState<T, E> = {
    error?: E;
    loading: boolean;
    value?: T;
};

type ErrorAction<E> = { type: 'error'; error: E };
type ResetAction<T> = { type: 'reset'; defaultValue?: T };
type ValueAction<T> = { type: 'value'; value?: T };
type ReducerAction<T, E> = ErrorAction<E> | ResetAction<T> | ValueAction<T>;

const defaultState = <T>(defaultValue?: T) => {
    return {
        loading: defaultValue === undefined || defaultValue === null,
        value: defaultValue,
    };
};

const reducer =
    <T, E>() =>
    (state: ReducerState<T, E>, action: ReducerAction<T, E>): ReducerState<T, E> => {
        switch (action.type) {
            case 'error':
                return {
                    ...state,
                    error: action.error,
                    loading: false,
                    value: undefined,
                };
            case 'reset':
                return defaultState(action.defaultValue);
            case 'value':
                return {
                    ...state,
                    error: undefined,
                    loading: false,
                    value: action.value,
                };
            default:
                return state;
        }
    };

export default function useLoadingValue<T, E>(getDefaultValue?: () => T): LoadingValue<T, E> {
    const defaultValue = getDefaultValue ? getDefaultValue() : undefined;
    const [state, dispatch] = useReducer(reducer<T, E>(), defaultState<T>(defaultValue));

    const reset = useCallback(() => {
        const defaultValue = getDefaultValue ? getDefaultValue() : undefined;
        dispatch({ type: 'reset', defaultValue });
    }, [getDefaultValue]);

    const setError = useCallback((error: E) => {
        dispatch({ type: 'error', error });
    }, []);

    const setValue = useCallback((value?: T) => {
        dispatch({ type: 'value', value });
    }, []);

    return useMemo(
        () => ({
            error: state.error,
            loading: state.loading,
            reset,
            setError,
            setValue,
            value: state.value,
        }),
        [state.error, state.loading, reset, setError, setValue, state.value],
    );
}
